/**
 * Вкладка «Договор» карточки сделки: договор — статус, прокат, клиент, даты,
 * документы, деньги — и товары с ценой, подшивом и выдачей. Как `dogovorList` +
 * `dogovorProductList` в CRM, без артикула. Спека
 * `docs/specs/2026-09-25-deal-contract-payments-design.md`; те же данные —
 * `prokat-runner deal-contract ID`.
 */
import { useQuery } from "@tanstack/react-query";
import { TriangleAlert } from "lucide-react";
import { type ReactNode, useState } from "react";

import { type Contract, type ContractProduct, type DealContract, loadDealContract } from "@/lib/dealContract";
import { NavMenu } from "@/nav/NavMenu";

const rub = (n: number) => `${n.toLocaleString("ru-RU")} ₽`;
const dt = (iso: string | null) => (iso ? `${iso.slice(8, 10)}.${iso.slice(5, 7)} ${iso.slice(11, 16)}`.trim() : "—");
const day = (iso: string | null) => (iso ? `${iso.slice(8, 10)}.${iso.slice(5, 7)}.${iso.slice(2, 4)}` : null);

function Stat({ label, value, hint, tone = "" }: { label: string; value: ReactNode; hint?: string; tone?: string }) {
  return (
    <div className="flex min-w-0 flex-col gap-1 rounded-[var(--radius-card)] border border-border bg-bg-subtle p-3" data-annot={`договор «${label}»`}>
      <div className="text-[12px] text-fg-muted">{label}</div>
      <div className={`text-[20px] font-semibold leading-none tabular-nums ${tone}`}>{value}</div>
      {hint && <div className="text-[11.5px] text-fg-faint">{hint}</div>}
    </div>
  );
}

function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex min-w-0 flex-col">
      <span className="text-[11.5px] text-fg-faint">{label}</span>
      <span className="text-[12.5px]">{children}</span>
    </div>
  );
}

function Rent({ r, missing }: { r: { start: string | null; end: string | null }; missing?: boolean }) {
  if (!r.start && !r.end) return <span className={missing ? "text-err" : "text-fg-faint"}>нет дат</span>;
  return (
    <span className="tabular-nums">
      {day(r.start) ?? <span className="text-err">старт нет</span>} – {day(r.end) ?? <span className="text-err">конец нет</span>}
    </span>
  );
}

function ProductRow({ p }: { p: ContractProduct }) {
  const tags = [
    p.is_sale ? "продажа" : p.service ? "услуга" : "прокат",
    p.is_select ? "на выбор" : null,
  ].filter(Boolean);
  return (
    <tr className={`border-t border-border align-top ${p.deleted ? "text-fg-faint" : ""}`} data-annot={`товар ${p.serial ?? p.name}`}>
      <td className="px-3 py-1.5">
        <div className={p.deleted ? "line-through" : ""}>
          {p.name} {p.serial && <span className="font-mono text-fg-muted">· {p.serial}</span>}
        </div>
        {p.category && <div className="text-[11.5px] text-fg-faint">{p.category}</div>}
        {p.comment && <div className="text-[12px] text-warn">«{p.comment}»</div>}
      </td>
      <td className="px-3 py-1.5 text-[12px]">
        {tags.map((t) => (
          <span key={t} className={`mr-1.5 ${t === "продажа" || t === "на выбор" ? "text-err" : ""}`}>
            {t}
          </span>
        ))}
        {/* Кто и когда выдал / вернул — по `sMove_deal` (спека `2026-10-23-contract-issue-history-design.md`). */}
        {p.state && (
          <div className={p.state === "выдан" ? "text-accent" : p.state === "возвращён" ? "text-ok" : "text-err"}>
            {p.state}
            {p.moved?.at ? ` ${day(p.moved.at)} ${p.moved.at.slice(11, 16)}` : ""}
            {p.moved?.who ? ` · ${p.moved.who}` : ""}
          </div>
        )}
      </td>
      <td className="whitespace-nowrap px-3 py-1.5 text-right tabular-nums">
        <div className={p.deleted ? "" : "font-medium"}>{rub(p.price)}</div>
        {p.price_type && (
          <div className="text-[11.5px]" style={p.price_color && !p.deleted ? { color: p.price_color } : undefined}>
            {p.price_type}
          </div>
        )}
      </td>
      <td className="px-3 py-1.5 text-[12px]">
        {p.tailor ? (
          <>
            <div className="text-ok">подшив · {p.tailor.status ?? "—"}</div>
            {p.tailor.who && <div className="text-fg-muted">{p.tailor.who}</div>}
            {p.tailor.delayed && <div className="text-warn">отложен {dt(p.tailor.delayed)}</div>}
          </>
        ) : (
          <span className="text-fg-faint">—</span>
        )}
      </td>
      <td className="px-3 py-1.5 text-[12px] text-fg-muted">
        <div>
          {dt(p.added.at)} · {p.added.who ?? "—"}
        </div>
        {p.deleted && (
          <div className="text-err">
            удалён {dt(p.deleted.at)} · {p.deleted.who ?? "—"}
          </div>
        )}
      </td>
    </tr>
  );
}

function ContractBlock({ c }: { c: Contract }) {
  const [showDeleted, setShowDeleted] = useState(false);
  const live = c.products.filter((p) => !p.deleted);
  const deleted = c.products.filter((p) => p.deleted);
  return (
    <section className="flex flex-col gap-2 rounded-[var(--radius-card)] border border-border p-3" data-annot={`договор ${c.id}`}>
      <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
        <h2 className="text-[14px] font-medium">Договор {c.id}</h2>
        <span className="text-[12.5px] font-medium" style={c.status_color ? { color: c.status_color } : undefined}>
          {c.status ?? "—"}
        </span>
        <span className="text-[12.5px] text-fg-muted">
          {[c.category, c.group, c.city].filter(Boolean).join(" · ")}
        </span>
        <NavMenu r={{ obj: "dogovorID", id: c.id }} />
        {c.customer.id && <NavMenu r={{ obj: "customerID", id: c.customer.id }} />}
      </div>
      {c.warnings.length > 0 && (
        <div className="flex flex-wrap items-center gap-x-2 text-[12px] font-medium text-err">
          <TriangleAlert size={12} aria-hidden />
          {c.warnings.join(" · ")}
        </div>
      )}
      <div className="grid grid-cols-2 gap-x-4 gap-y-2 sm:grid-cols-4">
        <Field label="Клиент">{c.customer.name ?? "—"}</Field>
        <Field label="Прокат в договоре">
          <Rent r={c.rent} missing />
        </Field>
        <Field label="Прокат в сделке">
          <Rent r={c.deal_rent} />
        </Field>
        <Field label="Штраф">{c.fine ?? "—"}</Field>
        <Field label="Создан">
          {dt(c.created.at)} · {c.created.who ?? "—"}
        </Field>
        <Field label="Обновлён">
          {dt(c.updated.at)} · {c.updated.who ?? "—"}
        </Field>
        <Field label="Напечатан">{dt(c.printed)}</Field>
        <Field label="Синхронизация с Bitrix">
          <span className={c.sync_stale ? "text-err" : ""}>{dt(c.synced)}</span>
        </Field>
        <Field label="Документы">{c.docs.length ? c.docs.join(", ") : <span className="text-fg-faint">нет</span>}</Field>
        <Field label="Итог договора">
          <span className="font-medium tabular-nums">{rub(c.money.total)}</span>
          <span className="text-fg-muted"> · позиций {c.money.positions}</span>
        </Field>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-[12.5px]" data-annot={`товары договора ${c.id}`}>
          <thead className="text-left text-[12px] text-fg-muted">
            <tr>
              <th className="px-3 py-1 font-normal">Товар</th>
              <th className="px-3 py-1 font-normal">Тип</th>
              <th className="px-3 py-1 text-right font-normal">Цена</th>
              <th className="px-3 py-1 font-normal">Подшив</th>
              <th className="px-3 py-1 font-normal">Добавил</th>
            </tr>
          </thead>
          <tbody>
            {live.map((p) => (
              <ProductRow key={p.id} p={p} />
            ))}
            {showDeleted && deleted.map((p) => <ProductRow key={p.id} p={p} />)}
          </tbody>
        </table>
      </div>
      {deleted.length > 0 && (
        <button type="button" onClick={() => setShowDeleted((v) => !v)} className="self-start text-[12px] text-accent hover:underline">
          {showDeleted ? "скрыть удалённые" : `показать удалённые (${deleted.length})`}
        </button>
      )}
    </section>
  );
}

function Body({ d }: { d: DealContract }) {
  if (!d.contracts.length) return <div className="text-[12.5px] text-fg-muted">У сделки нет договора в CRM.</div>;
  const m = d.money;
  return (
    <>
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
        <Stat label="Итог договора" value={rub(m.total)} hint="сумма цен товаров, без удалённых" />
        <Stat label="Оплачено" value={rub(m.paid)} tone="text-ok" hint="статусы «Оплачен» и «Подтверждено»" />
        <Stat label="К оплате" value={rub(m.due)} tone={m.due > 0 ? "text-warn" : ""} />
        <Stat label="Процент оплаты" value={m.percent === null ? "—" : `${m.percent}%`} />
      </div>
      {d.contracts.map((c) => (
        <ContractBlock key={c.id} c={c} />
      ))}
    </>
  );
}

export function DealContractTab({ dealId }: { dealId: number }) {
  const q = useQuery({ queryKey: ["deal-contract", dealId], queryFn: () => loadDealContract(dealId) });
  if (q.isPending) return <div className="text-[12.5px] text-fg-muted">Загружаю договор из CRM…</div>;
  if (q.isError) return <div className="text-[12.5px] text-err">Не удалось получить договор: {String(q.error.message)}</div>;
  return <Body d={q.data} />;
}
